import type { Consignment, HelmInquiryStatus, HelmOverview, Inquiry } from '../../lib/api/types'
import { shouldLoadSeaTracking } from './tracking'

/**
 * Headline counts from GET /overview, or - when it is unavailable - from the
 * rows on hand, which is what they always were and which stop at a list's
 * first page. Relative imports so the root test suite can load this module.
 */
export interface DashboardStats {
  totalConsignments: number
  openInquiries: number
  pendingEstimates: number
  seaTracked: number
  totalTaxEstimated: number
}

const CLOSED_INQUIRY: HelmInquiryStatus[] = ['COMPLETED', 'CANCELLED']

export function dashboardStats(
  overview: HelmOverview | null,
  consignments: Consignment[],
  inquiries: Inquiry[],
): DashboardStats {
  if (overview) {
    const closed = CLOSED_INQUIRY.reduce(
      (sum, status) => sum + (overview.inquiries.by_status[status] ?? 0),
      0,
    )
    return {
      totalConsignments: overview.consignments.total,
      openInquiries: overview.inquiries.total - closed,
      pendingEstimates: overview.consignments.without_tax_estimate,
      seaTracked: overview.consignments.sea_tracked,
      totalTaxEstimated: overview.consignments.tax_estimate_total,
    }
  }

  return {
    totalConsignments: consignments.length,
    openInquiries: inquiries.filter((inquiry) => !CLOSED_INQUIRY.includes(inquiry.status)).length,
    // Used to be status === 'pending', which the status enum cannot hold: always 0.
    pendingEstimates: consignments.filter((consignment) => consignment.total_tax_estimate == null)
      .length,
    seaTracked: consignments.filter(shouldLoadSeaTracking).length,
    totalTaxEstimated: consignments.reduce(
      (sum, consignment) => sum + (consignment.total_tax_estimate ?? 0),
      0,
    ),
  }
}

export function inquiryStatusCount(
  overview: HelmOverview | null,
  rows: Pick<Inquiry, 'status'>[],
  status: HelmInquiryStatus,
): number {
  if (overview) return overview.inquiries.by_status[status] ?? 0
  return rows.filter((inquiry) => inquiry.status === status).length
}
